import { ApolloQueryResult } from '@apollo/client';
import { IApolloConnectionEdge } from '@netfront/common-library';

import { GET_PROJECT_USAGE_QUERY } from './useGetProjectUsage.graphql';
import { getUpdatedProjectUsage } from './useGetProjectUsage.helpers';
import {
  IGetProjectUsageQueryGraphQLResponse,
  IGetProjectUsageQueryVariables,
  IHandleFetchMoreProjectUsageParams,
  IHandleGetProjectUsageParams,
  IUseGetProjectUsage,
  IUseGetProjectUsageOptions,
} from './useGetProjectUsage.interfaces';

import { IProjectUsage } from '../../../interfaces';
import { useKanziLazyQuery } from '../../apollo';

const useGetProjectUsage = (options?: IUseGetProjectUsageOptions): IUseGetProjectUsage => {
  const { fetchPolicy, onCompleted, onError, query, token } = options ?? ({} as IUseGetProjectUsageOptions);

  const [executeGetProjectUsage, { fetchMore, loading: isLoading }] = useKanziLazyQuery<
    IGetProjectUsageQueryGraphQLResponse,
    IGetProjectUsageQueryVariables
  >({
    options: {
      fetchPolicy: fetchPolicy ?? 'cache-and-network',
      onCompleted: (data) => {
        if (!onCompleted) {
          return;
        }

        const {
          report: {
            getProjectUsage: { edges, pageInfo, totalCount },
          },
        } = data;

        const updatedEdges = edges.map((edge) => {
          const { node } = edge;

          const updatedEdge: IApolloConnectionEdge<IProjectUsage> = {
            ...edge,
            node: getUpdatedProjectUsage(node),
          };

          return updatedEdge;
        });

        onCompleted({
          edges: updatedEdges,
          pageInfo,
          totalCount,
        });
      },
      onError,
    },
    query: query ?? GET_PROJECT_USAGE_QUERY,
    token,
  });

  const handleFetchMoreProjectUsage = ({
    after,
    filter,
    first,
    from,
    ianaTimeZone,
    projectId,
    to,
  }: IHandleFetchMoreProjectUsageParams): Promise<ApolloQueryResult<IGetProjectUsageQueryGraphQLResponse>> => {
    return fetchMore({
      updateQuery: (
        previousQueryResult,
        {
          fetchMoreResult: {
            report: { getProjectUsage: newlyFetchedProjectUsageConnection },
          },
        },
      ) => {
        const { edges: newlyFetchedEdges } = newlyFetchedProjectUsageConnection;

        if (!newlyFetchedEdges.length) {
          return previousQueryResult;
        }

        const {
          report: { getProjectUsage: previouslyFetchedProjectUsageConnection },
        } = previousQueryResult;

        const { edges: previouslyFetchedEdges } = previouslyFetchedProjectUsageConnection;

        return {
          ...previousQueryResult,
          report: {
            ...previouslyFetchedProjectUsageConnection,
            getProjectUsage: {
              ...newlyFetchedProjectUsageConnection,
              edges: [...previouslyFetchedEdges, ...newlyFetchedEdges],
            },
          },
        };
      },
      variables: {
        after,
        filter,
        first,
        from,
        ianaTimeZone,
        projectId,
        to,
      },
    });
  };

  const handleGetProjectUsage = ({ after, filter, first, from, ianaTimeZone, projectId, to }: IHandleGetProjectUsageParams) => {
    executeGetProjectUsage({
      variables: {
        after,
        filter,
        first,
        from,
        ianaTimeZone,
        projectId,
        to,
      },
    });
  };

  return {
    handleFetchMoreProjectUsage,
    handleGetProjectUsage,
    isLoading,
  };
};

export { useGetProjectUsage };
